import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  viewChild,
} from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import { ButtonComponent } from '../button/button.component';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { StateService } from '../../services/state.service';
import { RouterLink } from '@angular/router';
import { LanguagePopupService } from '../language-popup/language-popup.service';
import { Popup, promoDialogContent } from './promo-dialog-content';
import { AddLocalePipe } from '../../pipes/url-locale.pipe';
import { distinctUntilChanged, filter, skip } from 'rxjs';
import { SsrCookieService } from 'ngx-cookie-service-ssr';
import { LocalStorageService } from '../../services/local-storage.service';
import { IS_SERVER } from '../../providers/is-platform.provider';

const SEVEN_DAYS = 604800000;

@Component({
  selector: 'app-promo-dialog',
  standalone: true,
  imports: [
    TranslocoDirective,
    ButtonComponent,
    SvgIconComponent,
    RouterLink,
    AddLocalePipe,
  ],
  templateUrl: './promo-dialog.component.html',
  styleUrl: './promo-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PromoDialogComponent implements AfterViewInit {
  private readonly state = inject(StateService);
  private readonly cookies = inject(SsrCookieService);
  private readonly popupService = inject(LanguagePopupService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly locale = this.state.getActiveLocale();
  private readonly localStorage = inject(LocalStorageService);
  private readonly isServer = inject(IS_SERVER);

  readonly promoDialog = viewChild.required<ElementRef<HTMLDialogElement>>('promoDialog');

  popupContent?: Popup;
  popupStorage: null | string = null;

  ngAfterViewInit(): void {
    if (this.isServer || !this.promoDialog()) {
      return;
    }

    const checkConsent = this.cookies.get('CookieConsent');
    this.popupStorage = this.localStorage.getItem('promoDialog');
    this.popupContent = promoDialogContent[this.locale];

    if (checkConsent.length > 0) {
      this.initPopup();
    } else {
      this.popupService.isOpen$
        .pipe(
          skip(1),
          distinctUntilChanged(),
          filter((value) => !value),
        )
        .subscribe(() => {
          this.initPopup();
        });
    }
  }

  initPopup(): void {
    if (!this.popupContent) {
      return;
    }

    if (!this.popupStorage || new Date() >= new Date(this.popupStorage)) {
      setTimeout(() => {
        const expirationDate = new Date();
        expirationDate.setTime(expirationDate.getTime() + SEVEN_DAYS);
        this.localStorage.setItem(
          'promoDialog',
          JSON.stringify(expirationDate.toISOString()),
        );

        this.promoDialog().nativeElement.showModal();
        this.cdr.markForCheck();
      }, 4000);
    }
  }

  close(): void {
    this.promoDialog().nativeElement.close();
  }
}
